import { SelectOption } from "../../../molecule/inputs/select-input"
import { CSSProperties, useMemo } from "react"
import useEnterSearch from "hooks/useEnterSearch"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import { IProjectProfitabilityQueryParam } from "models/project"
import SearchSelect from "Components/molecule/inputs/search-select"
import MultiSelect from "Components/molecule/inputs/multi-select"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import TextInput from "Components/molecule/inputs/text-input"

interface SearchProjectProfitabilityProps {
    handleChangeQuery: (updates: Partial<IProjectProfitabilityQueryParam>) => any,
    query: IProjectProfitabilityQueryParam
    projectManagers: SelectOption[]
    customers: SelectOption[]
    branches: SelectOption[]
    status: SelectOption[]
    department: SelectOption[]
    fiscalPeriodOptions: SelectOption[]
    handleClearSearch: () => any
    handleSearch: () => any
    style?: CSSProperties
    hide?: {
        customers?: boolean,
        status?: boolean,
        department?: boolean,
        code?: boolean
    },
    isSearching: boolean
}

const SearchProjectProfitabilityForm = ({
    query,
    handleChangeQuery,
    projectManagers,
    branches,
    customers,
    status,
    department,
    fiscalPeriodOptions,
    handleClearSearch,
    handleSearch,
    hide,
    isSearching
}: SearchProjectProfitabilityProps) => {

    const { inputProps, selectProps } = useEnterSearch(handleSearch)

    const customerValue = useMemo(() => {
        const ids: string[] = (query.clientIds as string[]) ?? []
        return customers.filter(o => ids.includes(o.value))
    }, [query.clientIds, customers])

    const branchValue = useMemo(() => {
        const ids: string[] = (query.branchIds as string[]) ?? []
        return branches.filter(o => ids.includes(o.value))
    }, [query.branchIds, branches])

    const departmentValue = useMemo(() => {
        const ids: string[] = (query.departmentIds as string[]) ?? []
        return department.filter(o => ids.includes(o.value))
    }, [query.departmentIds, department])

    const projectManagerValue = useMemo(() => {
        const ids: string[] = (query.projectManagerIds as string[]) ?? []
        return projectManagers.filter(o => ids.includes(o.value))
    }, [query.projectManagerIds, projectManagers])

    const statusValue = useMemo(() => {
        const ids: string[] = (query.statusIds as string[]) ?? []
        return status.filter(o => ids.includes(o.value))
    }, [query.statusIds, status])

    const fromOptions = useMemo(() => {
        const toKey = query.toPeriod ?? null;
        return [
            { value: '', label: 'All' },
            ...fiscalPeriodOptions.filter(o => !toKey || Number(o.value) <= toKey)
        ];
    }, [query.toPeriod, fiscalPeriodOptions]);

    const toOptions = useMemo(() => {
        const fromKey = query.fromPeriod ?? null;
        return [
            { value: '', label: 'All' },
            ...fiscalPeriodOptions.filter(o => !fromKey || Number(o.value) >= fromKey)
        ];
    }, [query.fromPeriod, fiscalPeriodOptions]);

    return <SearchFormContainer
        inputs={<>
            {/* Period first, then Branch > Department, then the rest */}
            <div style={{
                minWidth: '250px',
                maxWidth: '350px',
                display: 'flex',
                flexDirection: 'row',
                gap: '10px'
            }}>
                <SearchSelect
                    className="col"
                    label={"From"}
                    value={query.fromPeriod ? String(query.fromPeriod) : ''}
                    onChange={(e) => {
                        handleChangeQuery({ fromPeriod: e?.value ? Number(e.value) : undefined });
                    }}
                    options={fromOptions}
                    {...selectProps}
                />
                <SearchSelect
                    className="col"
                    label={"To"}
                    value={query.toPeriod ? String(query.toPeriod) : ''}
                    onChange={(e) => {
                        handleChangeQuery({ toPeriod: e?.value ? Number(e.value) : undefined });
                    }}
                    options={toOptions}
                    {...selectProps}
                />
            </div>
            <MultiSelect
                className="col"
                label={"Branch"}
                value={branchValue}
                onChange={(selected) => handleChangeQuery({ branchIds: selected.map(s => s.value) })}
                options={branches}
                {...selectProps}
            />
            {
                !hide?.department &&
                <MultiSelect
                    className="col"
                    label={"Department"}
                    value={departmentValue}
                    onChange={(selected) => handleChangeQuery({ departmentIds: selected.map(s => s.value) })}
                    options={department}
                    {...selectProps}
                />
            }
            {
                !hide?.code &&
                <TextInput
                    className="col"
                    label={"Project Code"}
                    value={query.code ?? ""}
                    onChange={(e) => handleChangeQuery({ code: e?.target.value ?? '' })}
                    {...inputProps}
                    placeholder="Project Code"
                />
            }
            {
                !hide?.customers &&
                <MultiSelect
                    className="col"
                    label={"Customer"}
                    value={customerValue}
                    onChange={(selected) => handleChangeQuery({ clientIds: selected.map(s => s.value) })}
                    options={customers}
                    {...selectProps}
                />
            }
            <MultiSelect
                className="col"
                label={"Project Manager"}
                value={projectManagerValue}
                onChange={(selected) => handleChangeQuery({ projectManagerIds: selected.map(s => s.value) })}
                options={projectManagers}
                {...selectProps}
            />
            {
                !hide?.status &&
                <MultiSelect
                    className="col"
                    label={"Status"}
                    value={statusValue}
                    onChange={(selected) => handleChangeQuery({ statusIds: selected.map(s => s.value) })}
                    options={status}
                    {...selectProps}
                />
            }
        </>}
        clearSearchButton={
            <>
                <PrimaryButton
                    style={{ minWidth: '150px' }}
                    disabled={isSearching}
                    onClick={handleSearch}
                >
                    {isSearching ? 'Searching...' : 'Search'}
                </PrimaryButton>
                <SecondaryButton
                    style={{ minWidth: '100px' }}
                    disabled={isSearching}
                    onClick={handleClearSearch}
                >
                    Clear Search
                </SecondaryButton>
            </>
        }
    />
}

export default SearchProjectProfitabilityForm